import { useState, useEffect } from "react";
import { Settings, LogOut, Menu, X, ChevronDown, ChevronRight, Lock, User, Bell } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { usePersistState } from "../../context/PersistStateContext";
import { getDashboardItems, DashboardItem } from "../../api/dashboardService";
import { icons } from "../../assets/icons";
import { useNavigation } from "../../context/useNavigation";

interface SidebarItemSerializable {
  label: string;
  path: string;
  prioridad: number;
  ipp_ruta_foto: string;
  codigo?: string;
}

interface SidebarItem extends SidebarItemSerializable {
  icon: React.ReactNode;
  isConfigItem?: boolean;
  hasSubItems?: boolean;
  subItems?: ConfigSubItem[];
}

interface ConfigSubItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

// Función para generar una clave única de sesión
const generateSessionKey = (session: any): string => {
  if (!session?.email || !session?.rol?.id || !session?.unidad?.id) {
    return "no-session";
  }
  return `${session.email}-${session.rol.id}-${session.unidad.id}`;
};

const SIDEBAR_CACHE_VERSION = "v2";

const Sidebar = () => {
  const { session, isHydrating } = useAuth();
  const { saveState, loadState, clearState } = usePersistState();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleNavigation: handleCustomNavigation } = useNavigation();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string>(
    () => loadState<string>("sidebar_active") || "Panel de control"
  );
  const [expandedConfig, setExpandedConfig] = useState<boolean>(false);
  const [dashboardItems, setDashboardItems] = useState<SidebarItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [currentSessionKey, setCurrentSessionKey] = useState<string>("");

  // Opciones de configuración
  const configSubItems: ConfigSubItem[] = [
    {
      label: "Cambiar contraseña",
      path: "/ChangePasswordPage",
      icon: <Lock size={20} className="text-gray-600" />
    },
    {
      label: "Perfil de usuario",
      path: "/UserProfilePage",
      icon: <User size={20} className="text-gray-600" />
    }
  ];

  // Función para generar rutas dinámicas a partir de ipp_nombre
  const generatePath = (nombre: string, codigo: string): string => {
    const normalizedCode = (codigo || "").toUpperCase();
    const specificPaths: { [key: string]: string } = {
      PAG: "/PendingPayment",
      LPA: "/AdminConfigPage",
      PQR: "/pqr",
      DES: "/desperfectos",
      RDA: "/desperfectos",
      LVI: "/visitantes",
      MPE: "/mascotas-perdidas",
      VEH: "/vehiculos",
      DIN: "/tablero",
      ZCO: "/zonas-comunes",
      ASA: "/asambleas",
    };

    if (specificPaths[normalizedCode]) {
      return specificPaths[normalizedCode];
    }

    const slug = nombre
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
    return `/${slug}`;
  };

  // Función para manejar errores de imagen
  const handleImageError = (
    imagePath: string,
    e: React.SyntheticEvent<HTMLImageElement>
  ) => {
    console.warn(`Error cargando imagen: ${imagePath}`);
    setImageErrors((prev) => new Set(prev).add(imagePath));

    const imgElement = e.currentTarget;
    const fallbackIcon = imgElement.nextElementSibling as HTMLElement | null;

    if (fallbackIcon) {
      imgElement.style.display = "none";
      fallbackIcon.style.display = "block";
    }
  };

  // Función para crear el icono con manejo de errores mejorado
  const createIcon = (item: SidebarItemSerializable) => (
    <span className="relative flex items-center w-8 h-8">
      <img
        src={item.ipp_ruta_foto}
        alt={item.label}
        className="w-8 h-8 object-contain"
        style={{
          display: imageErrors.has(item.ipp_ruta_foto) ? "none" : "block",
        }}
        onError={(e) => handleImageError(item.ipp_ruta_foto, e)}
        onLoad={() => {
          setImageErrors((prev) => {
            const newSet = new Set(prev);
            newSet.delete(item.ipp_ruta_foto);
            return newSet;
          });
        }}
      />
      <Settings
        size={24}
        className={`absolute left-0 top-0 text-gray-600 ${
          imageErrors.has(item.ipp_ruta_foto) ? "block" : "hidden"
        }`}
      />
    </span>
  );

  // Función para mapear items con iconos
  const mapItemsWithIcons = (
    items: SidebarItemSerializable[]
  ): SidebarItem[] => {
    return items.map((item) => {
      const resolvedPath =
        item.codigo && item.codigo.trim() !== ""
          ? generatePath(item.label, item.codigo)
          : item.path;
      // Detectar si es el item de configuración ESPECÍFICAMENTE
      const isConfigItem = item.label.toLowerCase() === 'configuración' || 
                          item.label.toLowerCase() === 'config' ||
                          item.label.toLowerCase() === 'ajustes' ||
                          item.label.toLowerCase() === 'settings';

      return {
        ...item,
        path: resolvedPath,
        icon: createIcon(item),
        isConfigItem,
        hasSubItems: isConfigItem,
        subItems: isConfigItem ? configSubItems : undefined
      };
    });
  };

  // Función para limpiar caché de sesiones anteriores
  const clearOldSessionCache = (currentKey: string) => {
    const lastSessionKey = loadState<string>("last_session_key");

    if (lastSessionKey && lastSessionKey !== currentKey) {
      console.log("Limpiando caché de sesión anterior:", lastSessionKey);

      // Limpiar datos específicos de la sesión anterior
      clearState(`sidebar_items_${lastSessionKey}`);
      clearState(`sidebar_items_timestamp_${lastSessionKey}`);
      clearState(`sidebar_items_${SIDEBAR_CACHE_VERSION}_${lastSessionKey}`);
      clearState(`sidebar_items_timestamp_${SIDEBAR_CACHE_VERSION}_${lastSessionKey}`);
      clearState("sidebar_items"); // Legacy key
      clearState("sidebar_items_timestamp"); // Legacy key
    }

    // Guardar la nueva clave de sesión
    saveState("last_session_key", currentKey);
  };

  // Efecto para detectar cambios de sesión y limpiar caché
  useEffect(() => {
    if (session && !isHydrating) {
      const newSessionKey = generateSessionKey(session);

      // Si cambió la sesión, limpiar caché anterior
      if (newSessionKey !== currentSessionKey && currentSessionKey !== "") {
        console.log("Cambio de sesión detectado:", {
          anterior: currentSessionKey,
          nueva: newSessionKey,
        });
        clearOldSessionCache(newSessionKey);
        setDashboardItems([]); // Limpiar items actuales
        setImageErrors(new Set()); // Limpiar errores de imagen
        setExpandedConfig(false); // Cerrar configuración expandida
      }

      setCurrentSessionKey(newSessionKey);
    }
  }, [session, isHydrating, currentSessionKey]);

  // Cargar ítems del dashboard
  useEffect(() => {
    const fetchDashboardItems = async () => {
      if (
        isHydrating ||
        !session?.rol?.id ||
        !currentSessionKey ||
        currentSessionKey === "no-session"
      ) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        console.log(
          "Iniciando carga de dashboard items para sesión:",
          currentSessionKey
        );

        // Usar claves específicas por sesión
        const cacheKey = `sidebar_items_${SIDEBAR_CACHE_VERSION}_${currentSessionKey}`;
        const timestampKey = `sidebar_items_timestamp_${SIDEBAR_CACHE_VERSION}_${currentSessionKey}`;

        // Intentar cargar desde localStorage primero
        const storedItems = loadState<SidebarItemSerializable[]>(cacheKey);
        const storedTimestamp = loadState<number>(timestampKey);
        const currentTime = Date.now();
        const cacheValidTime = 5 * 60 * 1000; // 5 minutos

        // Usar caché si existe y es válido
        if (
          storedItems &&
          storedItems.length > 0 &&
          storedTimestamp &&
          currentTime - storedTimestamp < cacheValidTime
        ) {
          console.log("Cargando items desde caché de sesión:", storedItems);
          const mappedItems = mapItemsWithIcons(storedItems);
          setDashboardItems(mappedItems);
          setIsLoading(false);
          return;
        }

        // Consultar la API si no hay datos válidos en caché
        console.log("Consultando API con payload:", {
          "tipo usuario codigo": session.rol.id,
          "permiso para votar": 0,
        });

        const items = await getDashboardItems({
          "tipo usuario codigo": session.rol.id,
          "permiso para votar": 0,
        });

        console.log("Items recibidos de la API:", items);

        if (!items || items.length === 0) {
          setError("No se encontraron ítems para mostrar.");
          setIsLoading(false);
          return;
        }

        const serializableItems: SidebarItemSerializable[] = items
          .map((item) => ({
            label: item.ipp_nombre,
            ipp_ruta_foto: item.ipp_ruta_foto,
            path: generatePath(item.ipp_nombre, item.ipp_codigo),
            prioridad: item.ipp_prioridad,
            codigo: item.ipp_codigo,
          }))
          .sort((a, b) => a.prioridad - b.prioridad);

        // Mapear con iconos
        const mappedItems = mapItemsWithIcons(serializableItems);

        setDashboardItems(mappedItems);

        // Guardar en localStorage con claves específicas por sesión
        saveState(cacheKey, serializableItems);
        saveState(timestampKey, currentTime);

        console.log(
          "Items guardados en localStorage para sesión:",
          currentSessionKey,
          serializableItems
        );
      } catch (err) {
        console.error("Error al cargar dashboard items:", err);
        setError(
          "Error al cargar los ítems del dashboard: " + (err as Error).message
        );

        // Intentar cargar desde caché como fallback
        const fallbackItems = loadState<SidebarItemSerializable[]>(
          `sidebar_items_${SIDEBAR_CACHE_VERSION}_${currentSessionKey}`
        );
        if (fallbackItems && fallbackItems.length > 0) {
          console.log("Usando items de fallback desde caché de sesión");
          const mappedItems = mapItemsWithIcons(fallbackItems);
          setDashboardItems(mappedItems);
          setError(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardItems();
  }, [isHydrating, session?.rol?.id, currentSessionKey, saveState, loadState]);

  // Inicializar currentSessionKey al montar
  useEffect(() => {
    if (session && !isHydrating) {
      const sessionKey = generateSessionKey(session);
      setCurrentSessionKey(sessionKey);

      // Limpiar caché de sesiones anteriores
      clearOldSessionCache(sessionKey);

      // Intentar cargar items de esta sesión si existen
      const storedItems = loadState<SidebarItemSerializable[]>(
        `sidebar_items_${SIDEBAR_CACHE_VERSION}_${sessionKey}`
      );
      if (storedItems && storedItems.length > 0) {
        console.log(
          "Inicializando con items almacenados de sesión:",
          sessionKey,
          storedItems
        );
        const mappedItems = mapItemsWithIcons(storedItems);
        setDashboardItems(mappedItems);
      }
    }
  }, [session, isHydrating]);

  // Sincronizar el estado activo con la ruta actual
  useEffect(() => {
    if (dashboardItems.length === 0) return;

    const currentPath = location.pathname;
    
    // Buscar el item que coincide con la ruta actual
    const matchingItem = dashboardItems.find((item) => {
      // Comparar rutas exactas
      if (item.path === currentPath) return true;
      
      // También verificar sub-items de configuración
      if (item.isConfigItem && item.subItems) {
        return item.subItems.some((subItem) => subItem.path === currentPath);
      }
      
      return false;
    });

    if (matchingItem) {
      // Si es un sub-item de configuración, marcar el sub-item
      if (matchingItem.isConfigItem && matchingItem.subItems) {
        const matchingSubItem = matchingItem.subItems.find(
          (subItem) => subItem.path === currentPath
        );
        if (matchingSubItem) {
          setActive(matchingSubItem.label);
          saveState("sidebar_active", matchingSubItem.label);
          setExpandedConfig(true);
          return;
        }
      }
      
      // Marcar el item principal
      setActive(matchingItem.label);
      saveState("sidebar_active", matchingItem.label);
    }
  }, [location.pathname, dashboardItems, saveState]);

  // Función para manejar el clic en configuración
  const handleConfigClick = (item: SidebarItem) => {
    if (item.isConfigItem && item.hasSubItems) {
      setExpandedConfig(!expandedConfig);
      setActive(item.label);
      saveState("sidebar_active", item.label);
    } else {
      handleNavigation(item);
    }
  };

  // Función para manejar navegación de sub-items
  const handleSubItemNavigation = async (subItem: ConfigSubItem) => {
    const navigated = await handleCustomNavigation(subItem.path, subItem.label);
    
    if (navigated) {
      setActive(subItem.label);
      saveState("sidebar_active", subItem.label);
      setOpen(false);
    }
  };

  // Función actualizada para manejar navegación
  const handleNavigation = async (item: SidebarItem) => {
    // Intentar navegar usando el hook personalizado
    const navigated = await handleCustomNavigation(item.path, item.label);
    
    // Solo actualizar el estado si la navegación fue exitosa
    if (navigated) {
      setActive(item.label);
      saveState("sidebar_active", item.label);
      setOpen(false);
      setExpandedConfig(false); // Cerrar configuración al navegar a otra sección
    }
  };

  // Función para refrescar datos
  const refreshData = async () => {
    if (!currentSessionKey || currentSessionKey === "no-session") return;

    // Limpiar caché de la sesión actual
    clearState(`sidebar_items_${currentSessionKey}`);
    clearState(`sidebar_items_timestamp_${currentSessionKey}`);
    clearState(`sidebar_items_${SIDEBAR_CACHE_VERSION}_${currentSessionKey}`);
    clearState(`sidebar_items_timestamp_${SIDEBAR_CACHE_VERSION}_${currentSessionKey}`);
    setDashboardItems([]);
    setImageErrors(new Set());
    setExpandedConfig(false);

    // Recargar datos
    if (session?.rol?.id) {
      setIsLoading(true);
      // El useEffect se disparará automáticamente
    }
  };

  // Función para manejar el cierre de sesión
  const handleLogout = () => {
    // Limpiar todos los datos de caché al cerrar sesión
    console.log("Limpiando caché al cerrar sesión");

    if (currentSessionKey && currentSessionKey !== "no-session") {
      clearState(`sidebar_items_${currentSessionKey}`);
      clearState(`sidebar_items_timestamp_${currentSessionKey}`);
      clearState(`sidebar_items_${SIDEBAR_CACHE_VERSION}_${currentSessionKey}`);
      clearState(`sidebar_items_timestamp_${SIDEBAR_CACHE_VERSION}_${currentSessionKey}`);
    }

    // Limpiar también las claves legacy por si acaso
    clearState("sidebar_items");
    clearState("sidebar_items_timestamp");
    clearState("last_session_key");
    clearState("sidebar_active");

    // Navegar al logout
    navigate("/logout");
  };

  return (
    <>
      {/* Botón hamburguesa móvil */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white shadow rounded-lg"
        onClick={() => setOpen(!open)}
      >
        {open ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-[rgb(247,247,247)] shadow-lg flex flex-col
        transform transition-transform duration-300 z-40
        ${open ? "translate-x-0" : "-translate-x-full"}
        md:translate-x-0`}
      >
        {/* Header - ALTURA REDUCIDA AQUÍ */}
        <div className="flex items-center justify-between px-4 py-1 border-b bg-[rgb(247,247,247)]">
          <img
            src={icons.logo}
            alt="App Logo"
            className="w-32 h-auto object-contain"
          />
          <div className="flex gap-2">
            <button className="bg-red-500 text-white px-3 py-1 rounded-md font-semibold shadow hover:bg-red-600 transition text-sm">
              SOS
            </button>
          </div>
        </div>

        {/* User Info Section */}
        {session && (
          <div className="px-4 py-3 bg-[rgb(247,247,247)] border-b border-gray-300">
            <div className="bg-white rounded-md p-4 shadow-sm border border-gray-200">

              {/* Unidad Residencial */}
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-1 h-4 bg-green-500 rounded-sm"></div>
                  <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Unidad Residencial
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-900 leading-tight ml-3">
                  {session.unidad?.nombre || "Sin unidad"}
                </p>
              </div>

              {/* Nombre del Usuario */}
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-1 h-4 bg-green-500 rounded-sm"></div>
                  <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Usuario
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-900 leading-tight ml-3">
                  {session.usuario?.nombre || 
                   session.usuario?.nombreCompleto || 
                   session.usuario?.email || 
                   session.email || 
                   "Sin nombre"}
                </p>
              </div>

              {/* Rol */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-1 h-4 bg-green-500 rounded-sm"></div>
                  <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Rol
                  </span>
                </div>
                <div className="flex items-center justify-between ml-3">
                  <span className="text-sm font-semibold text-gray-900">
                    {session.rol?.nombre || "Sin rol"}
                  </span>
                  <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded font-mono border">
                    {session.rol?.id || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista con scroll */}
        <nav className="flex-1 overflow-y-auto mt-2 px-2">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-t-blue-500 border-gray-300 mb-2" />
              <p className="text-gray-600 text-sm">Cargando menú...</p>
            </div>
          ) : error ? (
            <div className="px-4 py-2">
              <p className="text-red-600 text-sm mb-2">{error}</p>
              <button
                onClick={refreshData}
                className="text-blue-500 text-sm underline hover:text-blue-700"
              >
                Intentar nuevamente
              </button>
            </div>
          ) : dashboardItems.length === 0 ? (
            <div className="px-4 py-2">
              <p className="text-gray-600 text-sm mb-2">
                No hay ítems disponibles.
              </p>
              <button
                onClick={refreshData}
                className="text-blue-500 text-sm underline hover:text-blue-700"
              >
                Refrescar
              </button>
            </div>
          ) : (
            <ul className="space-y-1">
              {dashboardItems.map((item, i) => (
                <li key={`${item.path}-${i}`}>
                  {/* Item principal */}
                  <button
                    onClick={() => item.isConfigItem ? handleConfigClick(item) : handleNavigation(item)}
                    className={`flex items-center gap-3 px-4 py-3 w-full text-left font-medium rounded-md transition text-base
                    ${
                      active === item.label
                        ? "bg-white shadow-md border-l-4 border-military-500 text-military-600"
                        : "text-gray-700 hover:bg-white hover:shadow"
                    }`}
                    title={`${item.isConfigItem ? 'Expandir' : 'Ir a'} ${item.label}`}
                  >
                    {item.icon}
                    <span className="truncate flex-1">{item.label}</span>
                    {item.isConfigItem && (
                      expandedConfig ? 
                        <ChevronDown size={20} className="text-gray-500" /> : 
                        <ChevronRight size={20} className="text-gray-500" />
                    )}
                  </button>

                  {/* Sub-items (collapse de configuración) */}
                  {item.isConfigItem && expandedConfig && (
                    <div className="mt-2 mb-2">
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        {configSubItems.map((subItem, subIndex) => (
                          <button
                            key={`${subItem.path}-${subIndex}`}
                            onClick={() => handleSubItemNavigation(subItem)}
                            className={`flex items-center gap-3 px-4 py-3 w-full text-left transition hover:bg-gray-50 border-b border-gray-100 last:border-b-0
                            ${
                              active === subItem.label
                                ? "bg-blue-50 text-blue-700 border-l-4 border-l-blue-500"
                                : "text-gray-700"
                            }`}
                            title={`Ir a ${subItem.label}`}
                          >
                            <div className="ml-4">
                              {subItem.icon}
                            </div>
                            <span className="text-sm font-medium">{subItem.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </nav>

        {/* Footer fijo con botón de cerrar sesión */}
        <div className="border-t bg-white px-4 py-3 shadow-sm space-y-2">
          <button
            className="flex items-center gap-3 px-4 py-3 w-full bg-red-500 text-white rounded-md shadow 
            hover:bg-red-600 transition font-semibold text-base"
            onClick={handleLogout}
          >
            <LogOut size={24} />
            Cerrar sesión
          </button>
          <div className="text-center">
            <p className="text-xs text-gray-500">Versión 1.0.0</p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;